import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";

export type CampaignReference = { kind: string; label: string; [key: string]: unknown };
export type CampaignTarget = { label: string; target: number; maximum?: number; cadence: string };
export type CampaignContextSettings = { campaign_id: string; brand_id: string; revision: number; references: CampaignReference[]; targets: CampaignTarget[]; updated_at: string | null };
export type DailyLoopConfig = {
  contract_version: 1; enabled: boolean; timezone: string; daily_time: string; freshness_hours: number; review_only: true;
};
export type DailyLoopSettings = { campaign_id: string; brand_id: string; revision: number; config: DailyLoopConfig | null; updated_at: string | null };
export type DailyLoopReadiness = { campaign_id: string; brand_id: string; resources: Array<{ reference_id: string; kind: string; label: string; status: string; detail: string; setup_path: string | null }> };
export type DailyLoopSettingsInput = Omit<DailyLoopConfig, "contract_version"> & { expected_revision: number };

const base = (brandId: string, campaignId: string) => `/api/v1/brands/${encodeURIComponent(brandId)}/campaigns/${encodeURIComponent(campaignId)}`;
function assertScope<T extends { brand_id: string; campaign_id: string }>(value: T, brandId: string, campaignId: string): T {
  if (value.brand_id.toLowerCase() !== brandId.toLowerCase() || value.campaign_id.toLowerCase() !== campaignId.toLowerCase())
    throw new Error("Daily setup returned a different brand or campaign.");
  return value;
}
export function useCampaignDailySetup(brandId: string, campaignId: string) {
  const root = base(brandId, campaignId);
  const context = useQuery({ queryKey: ["campaign-daily-setup-context", brandId, campaignId], retry: false, queryFn: async () => {
    const response = await api.get<{ data: CampaignContextSettings }>(`${root}/settings`);
    return assertScope(response.data, brandId, campaignId);
  } });
  const settings = useQuery({ queryKey: ["campaign-daily-loop-settings", brandId, campaignId], retry: false, queryFn: async () => {
    const response = await api.get<{ data: DailyLoopSettings }>(`${root}/daily-loop/settings`);
    return assertScope(response.data, brandId, campaignId);
  } });
  const readiness = useQuery({ queryKey: ["campaign-daily-loop-readiness", brandId, campaignId], retry: false, queryFn: async () => {
    const response = await api.get<{ data: DailyLoopReadiness }>(`${root}/daily-loop/readiness`);
    return assertScope(response.data, brandId, campaignId);
  } });
  return { context, settings, readiness };
}
export function useSaveCampaignDailyLoopSettings(brandId: string, campaignId: string) {
  const qc = useQueryClient();
  const root = base(brandId, campaignId);
  return useMutation({ mutationFn: (input: DailyLoopSettingsInput) => api.put<{ data: DailyLoopSettings }>(`${root}/daily-loop/settings`, input),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["campaign-daily-loop-settings", brandId, campaignId] }); void qc.invalidateQueries({ queryKey: ["campaign-daily-loop-readiness", brandId, campaignId] }); } });
}
